import { cn } from '@crowdex/ui/lib/utils';
import { Check, LoaderCircle, X } from 'lucide-react';
import { testRunDuration } from '@/lib/conversations/message-part/turn-steps';
import type { TerminalPart } from '@/lib/conversations/message-part/types';

/** The charter's card in a transcript: one summary, at most one action (design D10). */
export const TRANSCRIPT_CARD = 'rounded-card border border-hairline bg-card';

/**
 * A command's result as a card (design D10, "pnpm --filter web test · Tests 412 passed"): its
 * command, its summary line and how long it took. Its one action opens its terminal in the panel.
 */
export function CommandCard({
  command,
  summary,
  terminal,
  onOpen,
}: {
  command: string;
  summary: string;
  terminal: TerminalPart['data'];
  onOpen?: () => void;
}) {
  const running = terminal.exitCode === null && terminal.signal === null;
  const failed = !running && terminal.exitCode !== 0;
  const Icon = running ? LoaderCircle : failed ? X : Check;
  const duration = testRunDuration(terminal.output);
  const Root = onOpen === undefined ? 'div' : 'button';
  return (
    <Root
      className={cn(
        TRANSCRIPT_CARD,
        'flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left',
        onOpen !== undefined && 'transition-colors hover:border-foreground/20',
      )}
      data-command-card
      {...(onOpen === undefined
        ? {}
        : {
            onClick: onOpen,
            type: 'button' as const,
            'aria-label': `Open ${command} in Terminal`,
          })}
    >
      <Icon
        aria-hidden
        className={cn(
          'mt-0.5 size-3.5 shrink-0',
          running ? 'animate-spin text-running' : failed ? 'text-destructive' : 'text-success',
        )}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-mono text-foreground text-sm">{command}</span>
        <span
          className={cn(
            'mt-0.5 block truncate font-mono text-xs',
            failed ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {summary}
        </span>
      </span>
      {duration !== null && (
        <span className="type-meta shrink-0 text-muted-foreground tabular-nums">{duration}</span>
      )}
    </Root>
  );
}
